import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import {
  searchPatientsByDemographics,
  type PatientSearchItem,
  type PatientTelecomItem,
} from '@/features/auth/services/patientSearchApi'
import * as Sentry from '@sentry/react'

export interface PatientDemographicsLookupRequest {
  firstName: string
  lastName: string
  /** `YYYY-MM-DD` (e.g. from `<input type="date" />`); sent as Epic `birthdate` */
  dateOfBirth: string
}

export type PatientDemographicsLookupOutcome =
  | { status: 'found'; mrn: string; email?: string; phone?: string }
  /** One Epic Patient match, but no MRN on the resource (identifiers). */
  | { status: 'chart_found_mrn_missing'; email?: string; phone?: string }
  | { status: 'not_found' }
  | { status: 'ambiguous' }
  | { status: 'not_configured' }
  | { status: 'error'; message: string }

function telecomEmailPhone(telecom: PatientTelecomItem[]): { email?: string; phone?: string } {
  let email: string | undefined
  let phone: string | undefined
  for (const t of telecom) {
    const sys = (t.system ?? '').toLowerCase()
    const val = typeof t.value === 'string' ? t.value.trim() : ''
    if (!val) {
      continue
    }
    if (!email && (sys === 'email' || sys === 'mailto')) {
      email = val
    }
    if (!phone && (sys === 'phone' || sys === 'sms' || sys === 'fax')) {
      phone = val
    }
  }
  return { email, phone }
}

function mapSearchItemToContact(item: PatientSearchItem): {
  mrn: string
  email?: string
  phone?: string
} {
  const mrn = typeof item.mrn === 'string' ? item.mrn.trim() : ''
  const { email, phone } = telecomEmailPhone(item.telecom ?? [])
  return { mrn, email, phone }
}

/** True when Epic patient search can run (non-empty state and stored Epic session). */
export function isEpicPatientDemographicsLookupAvailable(epicState: string | undefined): boolean {
  const key = epicState?.trim() ?? ''
  if (!key) {
    return false
  }
  return EpicAuthClient.getInstance().hasSession(key)
}

/**
 * Resolve parent MRN (and optional email/phone from telecom) via POST /fhir/patient/search.
 * Requires an Epic SMART session (`epicState`).
 */
export async function lookupPatientByDemographics(
  epicState: string | undefined,
  body: PatientDemographicsLookupRequest,
  signal?: AbortSignal
): Promise<PatientDemographicsLookupOutcome> {
  const state = epicState?.trim() ?? ''
  if (!state || !EpicAuthClient.getInstance().hasSession(state)) {
    return { status: 'not_configured' }
  }

  const birthdate = body.dateOfBirth.trim()
  if (!birthdate) {
    return { status: 'error', message: 'Date of birth is required for patient lookup.' }
  }

  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /fhir/patient/search',
    },
    async (span) => {
      let items: PatientSearchItem[] | null
      try {
        items = await searchPatientsByDemographics(
          state,
          {
            given: body.firstName.trim(),
            family: body.lastName.trim(),
            birthdate,
          },
          signal
        )
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err
        }
        return { status: 'error', message: 'Patient lookup failed. Try again later.' }
      }

      if (items === null) {
        return { status: 'error', message: 'Patient lookup failed. Try again later.' }
      }

      if (items.length === 0) {
        return { status: 'not_found' }
      }

      if (items.length > 1) {
        span.setAttribute('match_count', items.length)
        return { status: 'ambiguous' }
      }

      const mapped = mapSearchItemToContact(items[0])
      const hasMrn = Boolean(mapped.mrn)
      if (!hasMrn) {
        span.setAttribute('mrn_present', false)
        return {
          status: 'chart_found_mrn_missing',
          email: mapped.email,
          phone: mapped.phone,
        }
      }

      span.setAttribute('mrn_present', true)
      return {
        status: 'found',
        mrn: mapped.mrn,
        email: mapped.email,
        phone: mapped.phone,
      }
    }
  )
}
